import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useCallback,
} from "react";
import { GetPageWrapperDataQuery } from "@graphqlTypes";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { getUrlLocale } from "@/core/utils";
import { isMonthInRange } from "@/utils/monthRange";

type BusinessHours = GetPageWrapperDataQuery["businessHours"];
type SpecialDate =
  BusinessHours["hours"]["specialHours"]["specialDates"][number];

interface DayRange {
  label: string;
  timeRange: string;
  openTime: string;
  closeTime: string;
  startDay: number;
  endDay: number;
}

interface DailyHours {
  startDateLabel?: string;
  timeRange: string;
  openTime: string;
  openTimeLabel: string;
  closeTime: string;
  closeTimeLabel: string;
  isCustom: boolean;
}

interface ProcessedMonthRange {
  monthLabel: string;
  startMonth: string;
  endMonth: string;
  startMonthName: string;
  endMonthName: string;
  startMonthIndex: number;
  endMonthIndex: number;
  dayRanges: DayRange[];
}

interface FormattedSpecialDate {
  label: string;
  timeRange: string;
  openTime?: string;
  endDate?: string;
  startDate?: string;
  closeTime?: string;
  relatedDates?: string[];
  isCustom: boolean;
}

interface BusinessHoursContextType {
  businessHours: BusinessHours | null;
  processedRanges: ProcessedMonthRange[];
  currentMonthRange: ProcessedMonthRange;
  afterBusinessHours: boolean;
  openTomorrow: boolean;
  openToday: boolean;
  beforeBusinessHours: boolean;
  currentlyOpen: boolean;
  currentDateTime: DateTime;
  todaysHours: DailyHours | null;
  nextOpenHours: DailyHours | null;
  specialDates: {
    label: string;
    dates: FormattedSpecialDate[];
  };
}
interface BusinessHoursProviderProps {
  children: ReactNode;
  businessHours: BusinessHours;
}

const BusinessHoursContext = createContext<
  BusinessHoursContextType | undefined
>(undefined);

const formatMonthRange = (startMonth: string, endMonth: string, locale: string): string => {
  const startDate = DateTime.fromISO(startMonth, { zone: "utc" }).setLocale(locale);
  const endDate = DateTime.fromISO(endMonth, { zone: "utc" }).setLocale(locale);

  const startMonthName = startDate.toFormat("LLLL");
  const endMonthName = endDate.toFormat("LLLL");

  return `${startMonthName} - ${endMonthName}`;
};

const getDayIndex = (day: string): number => {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return days.indexOf(day.toLowerCase());
};

const getDayName = (day: string, locale: string): string => {
  const dayIndex = getDayIndex(day); // 0 = Sunday, 6 = Saturday
  const date = DateTime.fromObject({ year: 2023, month: 1, day: 1 }, { locale }).plus({ days: dayIndex });
  return date.setLocale(locale).toFormat("cccc");
};

const formatTimeRange = (openTime: string, closeTime: string): string => {
  return `${openTime?.replace(":00 ", "")} - ${closeTime?.replace(":00 ", "")}`;
};

const groupConsecutiveDays = (
  daysWithHours: Array<{ day: string; openTime: string; closeTime: string }>,
  locale: string
): DayRange[] => {
  if (!daysWithHours.length) return [];

  const hourGroups: { [key: string]: string[] } = {};

  daysWithHours.forEach(({ day, openTime, closeTime }) => {
    const timeKey = `${openTime}-${closeTime}`;
    if (!hourGroups[timeKey]) {
      hourGroups[timeKey] = [];
    }
    hourGroups[timeKey].push(day);
  });

  const ranges: DayRange[] = [];

  Object.entries(hourGroups).forEach(([timeKey, days]) => {
    const [openTime, closeTime] = timeKey.split("-");
    const sortedDays = days.sort((a, b) => getDayIndex(a) - getDayIndex(b));

    const consecutiveGroups: string[][] = [];
    let currentGroup: string[] = [sortedDays[0]];

    for (let i = 1; i < sortedDays.length; i++) {
      const prevIndex = getDayIndex(sortedDays[i - 1]);
      const currentIndex = getDayIndex(sortedDays[i]);

      if (currentIndex === prevIndex + 1) {
        currentGroup.push(sortedDays[i]);
      } else {
        consecutiveGroups.push(currentGroup);
        currentGroup = [sortedDays[i]];
      }
    }
    consecutiveGroups.push(currentGroup);

    let label = "";
    consecutiveGroups.forEach((group, index) => {
      if (index > 0) label += ", ";

      if (group.length === 1) {
        label += getDayName(group[0], locale);
      } else if (group.length === 2) {
        // For two consecutive days, list them individually with &
        label += `${getDayName(group[0], locale)} & ${getDayName(group[1], locale)}`;
      } else {
        // For 3+ consecutive days, use hyphen format
        label += `${getDayName(group[0], locale)} – ${getDayName(
          group[group.length - 1],
          locale
        )}`;
      }
    });

    ranges.push({
      label,
      startDay: getDayIndex(sortedDays[0]),
      endDay: getDayIndex(sortedDays[sortedDays.length - 1]),
      timeRange: formatTimeRange(openTime, closeTime),
      openTime,
      closeTime,
    });
  });

  return ranges;
};

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatSpecialDate = (date: string, endDate?: string): string => {
  const startDate = DateTime.fromISO(date, { zone: "utc" });
  const dayName = startDate.toFormat("cccc");
  const monthName = startDate.toFormat("LLLL");

  const day = startDate.day;
  const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;

  if (endDate) {
    const endDateObj = DateTime.fromISO(endDate, { zone: "utc" });
    const endDay = endDateObj.day;
    const endDayWithSuffix = `${endDay}${getOrdinalSuffix(endDay)}`;
    return `${dayName}, ${monthName} ${dayWithSuffix} – ${endDayWithSuffix}`;
  }

  return `${dayName}, ${monthName} ${dayWithSuffix}`;
};

const processSpecialDates = (
  specialDates: SpecialDate[]
): FormattedSpecialDate[] => {
  if (!specialDates || !specialDates.length) return [];
  const mappedDates = specialDates
    .map((specialDate) => {
      let label = "";
      let timeRange = "";
      let isCustom = false;

      if (specialDate.dateType.includes("custom")) {
        // Custom dates use customLabel and customValue
        label = specialDate.customLabel || "Special Date";
        timeRange = specialDate.customValue || "CLOSED";
        isCustom = true;
      } else if (
        specialDate.dateType.includes("basic") &&
        specialDate.startDate
      ) {
        // Basic dates need to be formatted from the date
        label = formatSpecialDate(
          specialDate.startDate,
          specialDate.endDate || undefined
        );

        timeRange =
          specialDate.openTime && specialDate.closeTime
            ? formatTimeRange(specialDate.openTime, specialDate.closeTime)
            : "CLOSED";
        isCustom = false;
      }

      return {
        startDate: specialDate.startDate,
        endDate: specialDate.endDate,
        relatedDates: specialDate.relatedDates?.map((date) => date.date),
        label,
        openTime: specialDate.openTime,
        closeTime: specialDate.closeTime,
        timeRange,
        isCustom,
      };
    })
    .filter((item) => item.label && item.timeRange);

  return mappedDates
    .sort((a, b) => {
      if (!a.isCustom && !b.isCustom) {
        if (a?.startDate && b?.startDate) {
          const aDateTime = DateTime.fromISO(a.startDate, { zone: "utc" });
          const bDateTime = DateTime.fromISO(b.startDate, { zone: "utc" });
          return aDateTime.toMillis() - bDateTime.toMillis();
        }
      }
      // Custom dates go last
      if (a.isCustom && !b.isCustom) return 1;
      if (!a.isCustom && b.isCustom) return -1;
      return 0;
    })
    .map(
      ({
        isCustom,
        endDate,
        startDate,
        label,
        relatedDates,
        openTime,
        closeTime,
        timeRange,
      }) => ({
        label,
        timeRange,
        openTime,
        relatedDates,
        closeTime,
        startDate,
        endDate,
        isCustom,
      })
    );
};

const processBusinessHours = (
  businessHours: BusinessHours | null,
  locale: string
): ProcessedMonthRange[] => {
  const { monthRanges } = businessHours?.hours || {};
  if (!monthRanges) return [];

  return monthRanges.map((range) => {
    const startDate = DateTime.fromISO(range.startMonth || "", { zone: "utc" });
    const endDate = DateTime.fromISO(range.endMonth || "", { zone: "utc" });
    const spansYearBoundary = endDate.month < startDate.month;

    const monthLabel = formatMonthRange(
      range.startMonth || "",
      range.endMonth || "",
      locale
    );
    const startMonthName = startDate.toFormat("LLLL");
    const endMonthName = endDate.toFormat("LLLL");
    const startMonthIndex = startDate.month - 1;
    const endMonthIndex = spansYearBoundary ? (endDate.month - 1) + 12 : endDate.month - 1;

    const daysWithHours: Array<{
      day: string;
      openTime: string;
      closeTime: string;
    }> = [];
    const dailyHours = range.dailyHours;

    if (dailyHours) {
      Object.entries(dailyHours).forEach(([day, hours]) => {
        if (
          day !== "__typename" &&
          hours &&
          typeof hours === "object" &&
          "openTime" in hours &&
          "closeTime" in hours
        ) {
          if (hours.openTime && hours.closeTime) {
            daysWithHours.push({
              day,
              openTime: hours.openTime,
              closeTime: hours.closeTime,
            });
          }
        }
      });
    }

    const dayRanges = groupConsecutiveDays(daysWithHours, locale);

    return {
      monthLabel,
      startMonth: range.startMonth || "",
      endMonth: range.endMonth || "",
      startMonthName,
      endMonthName,
      startMonthIndex,
      endMonthIndex,
      dayRanges,
    };
  });
};

export const getCurrentRange = (processedRanges: ProcessedMonthRange[], currentDateTime: DateTime) => {
  return processedRanges.find((range) =>
    isMonthInRange(range.startMonth, range.endMonth, currentDateTime.month)
  );
}

// Get today's business hours based on the current date and time
// It checks for special dates first, then falls back to regular business hours
// Returns an object with timeRange, openTime, closeTime, and isCustom properties
// If no business hours are found, it returns null, but it should never be null
const getTodaysHours = (
  processedRanges: ProcessedMonthRange[],
  specialDates: FormattedSpecialDate[],
  currentDate: DateTime
) => {
  const currentMonth = currentDate.month - 1;
  const currentDay = currentDate.weekday % 7; // Luxon uses 1-7 for weekdays, where 1 is Monday and 7 is Sunday convert to 0-6
  const todayISO = currentDate.toISODate();
  const todaySpecialDate = specialDates.find((specialDate) => {
    if (specialDate.isCustom && specialDate.relatedDates) {
      const foundDate = specialDate.relatedDates.some((date) => {
        const dateISO = DateTime.fromISO(date, { zone: "utc" }).toISODate();
        return dateISO === todayISO;
      });

      if (foundDate) {
        return {
          startDate: specialDate.startDate,
          endDate: specialDate.endDate,
          relatedDates: specialDate.relatedDates,
          label: specialDate.label,
          openTime: null,
          closeTime: null,
          isCustom: true,
        };
      }
    }
    if (!specialDate.startDate) return false;

    const startDate = DateTime.fromISO(specialDate.startDate, {
      zone: "utc",
    }).toISODate();
    if (!specialDate.endDate) {
      return todayISO === startDate;
    }

    // If end date exists, check if today is between start and end (inclusive)
    const endDate = DateTime.fromISO(specialDate.endDate, {
      zone: "utc",
    }).toISODate();
    return todayISO >= startDate && todayISO <= endDate;
  });

  if (todaySpecialDate) {
    const startDate = DateTime.fromISO(todaySpecialDate.startDate, {
      zone: "utc",
    });
    const monthName = startDate.toFormat("LLL");
    const day = startDate.day;
    return {
      startDateLabel: `${monthName} ${day}`,
      timeRange: todaySpecialDate?.timeRange,
      openTime: todaySpecialDate?.openTime || "",
      closeTime: todaySpecialDate?.closeTime || "",
      openTimeLabel: todaySpecialDate?.openTime?.replace(":00 ", ""),
      closeTimeLabel: todaySpecialDate?.closeTime?.replace(":00 ", ""),
      isCustom: todaySpecialDate?.isCustom,
    };
  }
  const currentRange = getCurrentRange(processedRanges, currentDate);

  if (!currentRange) {
    console.error("No current month range found for today's date");
    return null;
  }

  const dayRange = currentRange.dayRanges.find(
    (range) => currentDay >= range.startDay && currentDay <= range.endDay
  );

  if (dayRange) {
    const startDate = DateTime.fromISO(currentDate.toISODate(), {
      zone: "utc",
    });
    const monthName = startDate.toFormat("LLL");
    const day = startDate.day;
    return {
      startDateLabel: `${monthName} ${day}`,
      timeRange: dayRange.timeRange,
      openTime: dayRange.openTime,
      openTimeLabel: dayRange.openTime.replace(":00 ", ""),
      closeTime: dayRange.closeTime,
      closeTimeLabel: dayRange.closeTime.replace(":00 ", ""),
      isCustom: false,
    };
  }
  console.error(
    "No business hours found for today's date",
    currentDate.toISODate()
  );
  return null;
};

// Helper function to get the current date and time, ensuring it has a valid time component
// If the date is provided as a string, it will be parsed; otherwise, the current date and time will be used
// If the date is invalid or has no time, it will default to the current time in 'America/New_York' timezone
const getCurrentDate = (
  currentDate: string | string[] | undefined
): DateTime => {
  let currentDateTime;
  if (currentDate && typeof currentDate === "string") {
    currentDateTime = DateTime.fromISO(currentDate, {
      zone: "America/New_York",
    });
    if (
      !currentDateTime.isValid ||
      (currentDateTime.hour === 0 &&
        currentDateTime.minute === 0 &&
        currentDateTime.second === 0)
    ) {
      const now = DateTime.now().setZone("America/New_York");
      currentDateTime = currentDateTime.set({
        hour: now.hour,
        minute: now.minute,
        second: now.second,
        millisecond: now.millisecond,
      });
    }
  } else {
    currentDateTime = DateTime.now().setZone("America/New_York");
  }
  return currentDateTime;
};

export const BusinessHoursProvider: React.FC<BusinessHoursProviderProps> = ({
  children,
  businessHours,
}) => {
  const router = useRouter();
  const {
    query: { current_date, debug_hours },
  } = router;
  const currentDateTime = useMemo(() => getCurrentDate(current_date), [current_date]);
  const debug = useMemo(() => debug_hours === "true", [debug_hours, router.query, router.isReady]);
  const [openTomorrow, setOpenTomrrow] = useState<boolean>(true);
  const { specialHours } = businessHours?.hours || {};
  const locale = useMemo(() => getUrlLocale(router.asPath), [router.asPath]);
  const debugLog = useCallback((message: string, data?: any) => {
    if (debug) {
      console.log(`[BusinessHoursProvider] ${message}`, data);
    }
  }, [debug]);

  const processedRanges = useMemo(() => {
    return processBusinessHours(businessHours, locale);
  }, [businessHours]);
  debugLog("Processed month ranges", processedRanges);

  const specialDates = useMemo(() => {
    return processSpecialDates(specialHours?.specialDates);
  }, [businessHours]);
  debugLog("Processed special dates", specialDates);
  // Process today's business hours based on the current date and time
  const todaysHours = useMemo(() => {
    debugLog("Processing today's business hours", {
      currentDateTime: currentDateTime.toISO(),
    });
    return getTodaysHours(processedRanges, specialDates, currentDateTime);
  }, [processedRanges, specialHours?.specialDates]);

  const openToday = useMemo(() => Boolean(todaysHours?.openTime && todaysHours?.closeTime), [todaysHours]);
  // process next open days business hours
  const nextOpenHours = useMemo(() => {
    let nextBusinessHours = null;
    let openTomorrow = true;
    let iterationMax = 30; // Limit iterations to prevent infinite loops
    let tomorrowDateTime = currentDateTime.plus({ days: 1 });
    while (!nextBusinessHours?.openTime && iterationMax > 0) {
      const nextDayHours = getTodaysHours(
        processedRanges,
        specialDates,
        tomorrowDateTime
      );
      if (nextDayHours?.openTime) {
        nextBusinessHours = nextDayHours;
      } else {
        debugLog(
          `No business hours found for ${tomorrowDateTime.toISODate()}, checking next day...`
        );
        tomorrowDateTime = tomorrowDateTime.plus({ days: 1 });
        debugLog(`Next date to check: ${tomorrowDateTime.toISODate()}`);
        openTomorrow = false;
      }
      iterationMax--;
    }
    debugLog("Next open hours found", nextBusinessHours);
    debugLog("Open tomorrow status", openTomorrow);
    setOpenTomrrow(openTomorrow);
    return nextBusinessHours;
  }, [processedRanges, specialHours?.specialDates, currentDateTime]);
  // Check if the current time is within today's business hours
  const currentlyOpen = useMemo(() => {
    if (!todaysHours) return false;
    const { openTime, closeTime } = todaysHours;
    if (!openTime || !closeTime) return false;
    const now = currentDateTime.setZone("America/New_York");
    const openDateTime = DateTime.fromFormat(
      `${currentDateTime.toISODate()} ${openTime}`,
      "yyyy-MM-dd h:mm a",
      { zone: "America/New_York" }
    );
    const closeDateTime = DateTime.fromFormat(
      `${currentDateTime.toISODate()} ${closeTime}`,
      "yyyy-MM-dd h:mm a",
      { zone: "America/New_York" }
    );
    const currentlyOpen = now >= openDateTime && now <= closeDateTime;
    debugLog(
      `Checking if currently open: ${now} >= ${openDateTime} && ${now} <= ${closeDateTime}`,
      { currentlyOpen }
    );
    return currentlyOpen;
  }, [todaysHours, currentDateTime]);

  // Check if the current time is after business hours
  const afterBusinessHours = useMemo(() => {
    if (!todaysHours) return false;
    const { openTime, closeTime } = todaysHours;
    if (!openTime || !closeTime) return false;
    const now = currentDateTime.setZone("America/New_York");
    const closeDateTime = DateTime.fromFormat(
      `${currentDateTime.toISODate()} ${closeTime}`,
      "yyyy-MM-dd h:mm a",
      { zone: "America/New_York" }
    );
    const afterBusinessHours = now > closeDateTime;
    debugLog(`Checking if after business hours: ${now} > ${closeDateTime}`, {
      afterBusinessHours,
    });
    return afterBusinessHours;
  }, [todaysHours, currentDateTime]);

  // Check if the current time is before business hours
  const beforeBusinessHours = useMemo(() => {
    if (!todaysHours) return false;
    const { openTime } = todaysHours;
    if (!openTime) return false;
    const now = currentDateTime.setZone("America/New_York");
    const openDateTime = DateTime.fromFormat(
      `${currentDateTime.toISODate()} ${openTime}`,
      "yyyy-MM-dd h:mm a",
      { zone: "America/New_York" }
    );
    const fourHoursBeforeOpen = openDateTime.minus({ hours: 4 });
    const beforeBusinessHours = now < openDateTime && now >= fourHoursBeforeOpen;
    debugLog(`Checking if before business hours (within 4 hours): ${now} < ${openDateTime} && ${now} >= ${fourHoursBeforeOpen}`, {
      beforeBusinessHours,
    });
    return beforeBusinessHours;
  }, [todaysHours, currentDateTime]);

    const currentMonthRange = useMemo(() => getCurrentRange(processedRanges, currentDateTime), [processedRanges, currentDateTime]);

  return (
    <BusinessHoursContext.Provider
      value={{
        specialDates: { dates: specialDates, label: specialHours?.label },
        currentDateTime,
        businessHours,
        processedRanges,
        nextOpenHours,
        openToday,
        currentMonthRange,
        openTomorrow,
        todaysHours,
        afterBusinessHours,
        beforeBusinessHours,
        currentlyOpen,
      }}
    >
      {children}
    </BusinessHoursContext.Provider>
  );
};

export const useBusinessHours = () => {
  const context = useContext(BusinessHoursContext);
  return context || {
    currentlyOpen: false,
    beforeBusinessHours: false,
    afterBusinessHours: false,
    openToday: false,
    openTomorrow: false,
    businessHours: null,
    currentDateTime: DateTime.now().setZone("America/New_York"),
    processedRanges: [],
    currentMonthRange: {
      monthLabel: "",
      startMonth: "",
      endMonth: "",
      startMonthName: "",
      endMonthName: "",
      startMonthIndex: 0,
      endMonthIndex: 0,
      dayRanges: [],
    },
    todaysHours: null,
    nextOpenHours: null,
    specialDates: { dates: [], label: "" }
  };
};
